import { motion, useScroll } from 'framer-motion';
import { useRef, useEffect, useState } from 'react';
import { Target, Lightbulb, Heart, Shield, Factory, Flag, UserCheck } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { companyInfo } from '../data';

const values = [
  {
    icon: Target,
    title: 'Fokus pada Hasil',
    description: 'Kami berkomitmen memberikan solusi yang terukur dan berdampak nyata bagi bisnis Anda.',
  },
  {
    icon: Lightbulb,
    title: 'Inovasi Berkelanjutan',
    description: 'Selalu mengikuti perkembangan terbaru untuk memberikan solusi yang modern dan efektif.',
  },
  {
    icon: Heart,
    title: 'Pelayanan dengan Hati',
    description: 'Kami memperlakukan setiap klien dengan perhatian dan dedikasi penuh seperti partner.',
  },
  {
    icon: Shield,
    title: 'Integritas Tinggi',
    description: 'Transparansi dan kejujuran dalam setiap aspek kerja sama yang kami bangun.',
  },
];

const achievements = [
  { icon: Factory, value: '15+', label: 'Sektor Industri' },
  { icon: Flag, value: '5+', label: 'Proyek Multi-Region' },
  { icon: UserCheck, value: '12', label: 'Ahli Berpengalaman' },
];

// Scroll-triggered dual video playback component
function ScrollVideo() {
  const containerRef = useRef<HTMLDivElement>(null);
  const forwardRef = useRef<HTMLVideoElement>(null);
  const reverseRef = useRef<HTMLVideoElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isForward, setIsForward] = useState(true);
  const scrollDirectionRef = useRef<'down' | 'up'>('down');
  const lastScrollYRef = useRef(0);
  const scrollProgressRef = useRef(0);
  const hasTriggeredRef = useRef(false);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  useEffect(() => {
    const forwardVideo = forwardRef.current;
    const reverseVideo = reverseRef.current;
    if (!forwardVideo || !reverseVideo || !isLoaded) return;

    const unsubscribe = scrollYProgress.on("change", (progress) => {
      scrollProgressRef.current = progress;
      
      // Detect scroll direction
      const currentScrollY = window.scrollY;
      const direction = currentScrollY > lastScrollYRef.current ? 'down' : 'up';
      
      // Thresholds: forward at 30%, reverse at 20%
      const isPastForwardThreshold = progress >= 0.3;
      const isBeforeReverseThreshold = progress <= 0.2;
      
      // Switch videos based on direction and position
      if (isPastForwardThreshold && !hasTriggeredRef.current && direction === 'down') {
        hasTriggeredRef.current = true;
        
        // Scrolling down past 40% - show forward video
        setIsForward(true);
        forwardVideo.currentTime = 0;
        forwardVideo.play();
        reverseVideo.pause();
      } else if (isBeforeReverseThreshold && hasTriggeredRef.current && direction === 'up') {
        hasTriggeredRef.current = false;
        
        // Scrolling up before 30% - show reverse video
        setIsForward(false);
        reverseVideo.currentTime = 0;
        reverseVideo.play();
        forwardVideo.pause();
      }
      
      lastScrollYRef.current = currentScrollY;
      scrollDirectionRef.current = direction;
    });

    // Monitor videos to pause at ends
    const handleForwardEnd = () => {
      if (forwardVideo) {
        forwardVideo.pause();
        forwardVideo.currentTime = forwardVideo.duration;
      }
    };
    
    const handleReverseEnd = () => {
      if (reverseVideo) {
        reverseVideo.pause();
        reverseVideo.currentTime = reverseVideo.duration;
      }
    };
    
    forwardVideo.addEventListener('ended', handleForwardEnd);
    reverseVideo.addEventListener('ended', handleReverseEnd);

    return () => {
      unsubscribe();
      forwardVideo.removeEventListener('ended', handleForwardEnd);
      reverseVideo.removeEventListener('ended', handleReverseEnd);
    };
  }, [scrollYProgress, isLoaded]);

  const handleLoaded = () => {
    setIsLoaded(true);
  };

  return (
    <motion.div
      ref={containerRef}
      className="relative h-full min-h-[400px] lg:min-h-[500px]"
      initial={{ opacity: 0 }}
      animate={{ opacity: isLoaded ? 1 : 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Forward Video (Hands → Shake) */}
      <video
        ref={forwardRef}
        src="/handshake-forward.mov"
        muted
        playsInline
        preload="auto"
        onLoadedMetadata={handleLoaded}
        className={`w-full h-full object-cover rounded-3xl absolute inset-0 transition-opacity duration-300 ${
          isForward ? 'opacity-100 z-10' : 'opacity-0 z-0'
        }`}
      />
      
      {/* Reverse Video (Shake → Hands) */}
      <video
        ref={reverseRef}
        src="/handshake-reverse.mov"
        muted
        playsInline
        preload="auto"
        onLoadedMetadata={handleLoaded}
        className={`w-full h-full object-cover rounded-3xl absolute inset-0 transition-opacity duration-300 ${
          !isForward ? 'opacity-100 z-10' : 'opacity-0 z-0'
        }`}
      />
      
      {/* Subtle overlay to blend with theme */}
      <div className="absolute inset-0 bg-gradient-to-t from-dark-900/30 via-transparent to-dark-900/20 rounded-3xl pointer-events-none z-20" />
    </motion.div>
  );
}

export function About() {
  const { ref, isInView } = useScrollAnimation({ threshold: 0.2 });

  return (
    <section
      id="about"
      ref={ref}
      className="relative py-24 lg:py-32 overflow-hidden"
    >
      {/* Animated Gradient Mesh Background - Enhanced */}
      <div className="absolute inset-0 bg-dark-900" />
      
      {/* Morphing Gradient Blobs - Highly Visible */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Blob 1 - Top Left - Large and Bright */}
        <motion.div
          animate={{
            x: [0, 200, 0],
            y: [0, 120, 0],
            scale: [1, 1.5, 1],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute -top-60 -left-60 w-[700px] h-[700px] bg-gradient-to-br from-primary-500/70 to-blue-600/50 rounded-full blur-[60px]"
        />
        
        {/* Blob 2 - Top Right - Cyan Glow */}
        <motion.div
          animate={{
            x: [0, -180, 0],
            y: [0, 200, 0],
            scale: [1, 1.6, 1],
          }}
          transition={{
            duration: 14,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
          className="absolute -top-40 -right-60 w-[600px] h-[600px] bg-gradient-to-br from-cyan-400/60 to-primary-500/40 rounded-full blur-[50px]"
        />
        
        {/* Blob 3 - Bottom Left - Purple Accent */}
        <motion.div
          animate={{
            x: [0, 150, 0],
            y: [0, -150, 0],
            scale: [1, 1.7, 1],
          }}
          transition={{
            duration: 11,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 3,
          }}
          className="absolute -bottom-40 -left-60 w-[650px] h-[650px] bg-gradient-to-tr from-primary-600/65 to-purple-500/45 rounded-full blur-[55px]"
        />
        
        {/* Blob 4 - Bottom Right - Blue Tint */}
        <motion.div
          animate={{
            x: [0, -200, 0],
            y: [0, -120, 0],
            scale: [1, 1.5, 1],
          }}
          transition={{
            duration: 13,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2,
          }}
          className="absolute -bottom-60 right-0 w-[550px] h-[550px] bg-gradient-to-bl from-blue-400/55 to-primary-600/40 rounded-full blur-[50px]"
        />

        {/* Blob 5 - Center - Strong Pulsing */}
        <motion.div
          animate={{
            scale: [1, 1.4, 1],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.5,
          }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-r from-primary-400/60 to-cyan-400/45 rounded-full blur-[70px]"
        />

        {/* Blob 6 - Additional Corner Accent */}
        <motion.div
          animate={{
            x: [100, -100, 100],
            y: [-50, 50, -50],
            scale: [1, 1.3, 1],
          }}
          transition={{
            duration: 16,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 4,
          }}
          className="absolute top-1/4 right-1/4 w-[300px] h-[300px] bg-gradient-to-br from-indigo-500/50 to-primary-400/35 rounded-full blur-[40px]"
        />
      </div>
      
      {/* Subtle Noise Texture Overlay */}
      <div 
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      <div className="relative max-w-7xl mx-auto section-padding">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full mb-6"
          >
            <span className="w-2 h-2 bg-primary-400 rounded-full" />
            <span className="text-sm font-medium text-white/90">Tentang Kami</span>
          </motion.div>

          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Membangun Kesuksesan
            <span className="text-gradient"> Bersama Anda</span>
          </h2>

          <p className="text-lg text-white/70">
            {companyInfo.description}
          </p>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-24">
          {/* Left: Scroll-controlled Handshake Video */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative">
              {/* Scroll Video Container */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
                className="relative z-10 glass rounded-3xl overflow-hidden shadow-2xl shadow-primary-500/10 p-4"
              >
                <ScrollVideo />
              </motion.div>

              {/* Decorative Elements */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
                className="absolute -top-8 -left-8 w-32 h-32 border-2 border-dashed border-primary-300 rounded-full opacity-50 -z-10"
              />
            </div>          
          </motion.div>

          {/* Right: Values */}
          <div className="space-y-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, x: 50 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                  whileHover={{ x: 10 }}
                  className="group flex gap-5 p-6 rounded-2xl transition-all duration-300 hover:bg-white/10 hover:shadow-xl hover:shadow-primary-500/5"
                >
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 bg-gradient-to-br from-primary-900/50 to-primary-800/30 rounded-xl flex items-center justify-center group-hover:from-primary-600 group-hover:to-primary-700 transition-all duration-300"
                    >
                      <Icon className="w-6 h-6 text-primary-400 group-hover:text-white transition-colors duration-300" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-primary-400 transition-colors duration-300"
                    >
                      {value.title}
                    </h3>
                    <p className="text-white/80 leading-relaxed">{value.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Achievement Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {achievements.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="glass rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl flex items-center justify-center shadow-lg shadow-primary-500/30"
                >
                  <Icon className="w-8 h-8 text-white" />
                </div>
                <p className="text-4xl font-bold text-gradient mb-2">{item.value}</p>
                <p className="text-white/80 font-medium">{item.label}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
